import React, { useContext, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { ReportsContext } from '../context';
import { RUN_REPORT } from './constants';
import { actionsMessages } from './messages';
import style from './style.module.less';
import { SHIPSTATION_ANALYTICS_DASHBOARD_EXPORT } from '../../../../../constants/analytics-events';
import {
  useIsShipStationAnalyticsFeatureEnabled,
  useGetShipStationAnalyticsUserType,
} from '../../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { useReportsDownloadFeatureGate } from '../../../../../feature-gates/use-reports-download-feature-gate';
import { ANALYTICS_VIEW_TYPE } from '../../../../../pages/insights/constants';
import { Segment } from '../../../../../services/analytics-services/segment';

/**
 * Component for displaying report actions
 * @param {object} props
 * @param {string[]} props.actions
 * @param {() => void} [props.runReportHandler]
 * @param {boolean} [props.isRunReportDisabled]
 * @returns {JSX.Element | null}
 */

const ReportActions = ({ actions, runReportHandler, isRunReportDisabled }) => {
  const { handleReportAction, isReportGenerationProcessing } = useContext(ReportsContext);
  const isReportGenerationDisabled = isReportGenerationProcessing || isRunReportDisabled;
  const runReportAction = runReportHandler ?? handleReportAction;
  const isShipStationAnalyticsEnabled = useIsShipStationAnalyticsFeatureEnabled();
  const shipStationAnalyticsSellerType = useGetShipStationAnalyticsUserType() || ANALYTICS_VIEW_TYPE.locked;

  const handleRunReport = useCallback(() => {
    runReportAction(RUN_REPORT);
  }, [runReportAction]);

  const handleRunReportWithAction = useCallback(
    (action) => {
      runReportAction(action);

      if (isShipStationAnalyticsEnabled) {
        const reportType = window.location.pathname.split('/').pop() || 'unknown';

        const payload = {
          shipStationAnalyticsSellerType,
          reportType,
          action,
        };

        Segment.trackEvent(SHIPSTATION_ANALYTICS_DASHBOARD_EXPORT, payload);
      }
    },
    [runReportAction, shipStationAnalyticsSellerType, isShipStationAnalyticsEnabled],
  );

  const { isFeatureEnabled, disabledMessage } = useReportsDownloadFeatureGate();
  const isDownloadDisabled = !isFeatureEnabled || isReportGenerationDisabled;
  const downloadDisabledTooltipMessage = disabledMessage ? <FormattedMessage {...disabledMessage} /> : undefined;

  return (
    <div className={style['report-actions']}>
      <Button disabled={isReportGenerationDisabled} onClick={handleRunReport} theme='primary'>
        <FormattedMessage {...actionsMessages[RUN_REPORT]} />
      </Button>
      <DropdownMenu disabled={isDownloadDisabled} disabledTooltipMessage={downloadDisabledTooltipMessage}>
        <DropdownMenuToggler className={style['menu-toggler']} theme='tertiary'>
          <FormattedMessage {...actionsMessages.runActionToggler} />
        </DropdownMenuToggler>
        {actions.map((action, index) => {
          return (
            <DropdownMenuItem key={index} name={action} onClick={handleRunReportWithAction}>
              <FormattedMessage {...actionsMessages[action]} />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenu>
    </div>
  );
};

export default ReportActions;
